import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Slider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { ThemeDecoration } from '../../../types';
import { DECORATION_POSITIONS } from './themeConstants';

export interface DecorationEditorProps {
  decorations: ThemeDecoration[];
  onChange: (decorations: ThemeDecoration[]) => void;
}

export function DecorationEditor({ decorations, onChange }: DecorationEditorProps) {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editDecoration, setEditDecoration] = useState<ThemeDecoration | null>(null);

  const handleAdd = () => {
    const newDecoration: ThemeDecoration = {
      type: 'emoji',
      content: '🎉',
      position: 'top-left',
      size: '40px',
      opacity: 1,
      offsetX: '10px',
      offsetY: '10px',
    };
    setEditDecoration(newDecoration);
    setEditIndex(decorations.length);
  };

  const handleEdit = (index: number) => {
    setEditDecoration({ ...decorations[index] });
    setEditIndex(index);
  };

  const handleDelete = (index: number) => {
    onChange(decorations.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (editDecoration === null || editIndex === null) return;
    const newDecorations = [...decorations];
    if (editIndex === decorations.length) {
      newDecorations.push(editDecoration);
    } else {
      newDecorations[editIndex] = editDecoration;
    }
    onChange(newDecorations);
    setEditIndex(null);
    setEditDecoration(null);
  };

  const handleCancel = () => {
    setEditIndex(null);
    setEditDecoration(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2">Decorations ({decorations.length})</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Decoration
        </Button>
      </Box>

      {decorations.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No decorations. Add emojis or images to enhance the theme.
        </Typography>
      )}

      <Stack spacing={1}>
        {decorations.map((dec, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ minWidth: 40 }}>
                  {dec.type === 'emoji' ? dec.content : '🖼️'}
                </Typography>
                <Box>
                  <Typography variant="body2">{dec.position}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dec.size} | {Math.round((dec.opacity ?? 1) * 100)}% opacity
                  </Typography>
                </Box>
              </Box>
              <Box>
                <IconButton size="small" onClick={() => handleEdit(index)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(index)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        ))}
      </Stack>

      {/* Decoration Edit Dialog */}
      <Dialog open={editIndex !== null} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{editIndex === decorations.length ? 'Add Decoration' : 'Edit Decoration'}</DialogTitle>
        <DialogContent>
          {editDecoration && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={editDecoration.type}
                  label="Type"
                  onChange={(e) =>
                    setEditDecoration({ ...editDecoration, type: e.target.value as ThemeDecoration['type'] })
                  }
                >
                  <MenuItem value="emoji">Emoji</MenuItem>
                  <MenuItem value="image">Image URL</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label={editDecoration.type === 'emoji' ? 'Emoji' : 'Image URL'}
                value={editDecoration.content}
                onChange={(e) => setEditDecoration({ ...editDecoration, content: e.target.value })}
                placeholder={editDecoration.type === 'emoji' ? '🎄' : 'https://...'}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Position</InputLabel>
                <Select
                  value={editDecoration.position}
                  label="Position"
                  onChange={(e) =>
                    setEditDecoration({ ...editDecoration, position: e.target.value as ThemeDecoration['position'] })
                  }
                >
                  {DECORATION_POSITIONS.map((pos) => (
                    <MenuItem key={pos} value={pos}>
                      {pos}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Size (CSS)"
                value={editDecoration.size || ''}
                onChange={(e) => setEditDecoration({ ...editDecoration, size: e.target.value })}
                placeholder="40px"
              />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Opacity: {Math.round((editDecoration.opacity ?? 1) * 100)}%
                </Typography>
                <Slider
                  size="small"
                  value={editDecoration.opacity ?? 1}
                  min={0}
                  max={1}
                  step={0.05}
                  onChange={(_, value) => setEditDecoration({ ...editDecoration, opacity: value as number })}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label="Offset X"
                  value={editDecoration.offsetX || ''}
                  onChange={(e) => setEditDecoration({ ...editDecoration, offsetX: e.target.value })}
                  placeholder="10px"
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Offset Y"
                  value={editDecoration.offsetY || ''}
                  onChange={(e) => setEditDecoration({ ...editDecoration, offsetY: e.target.value })}
                  placeholder="10px"
                  sx={{ flex: 1 }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
